import memoize from 'memoize-one';
import { uniqBy, first, pick } from 'lodash';
import moment from 'moment';
import messages from './reportApplicationMessages';

export const paginate = memoize((array, page, limit) =>
  array.slice((page - 1) * limit, page * limit),
);

export const filterEvaluations = memoize((evaluations, filter, year) =>
  evaluations.filter((evaluation) => {
    const matchIdCSchool =
      [null, undefined, ''].includes(filter.idCSchool) || evaluation.idCSchool === filter.idCSchool;
    const matchCourse =
      [null, undefined, ''].includes(filter.idCourse) || evaluation.idCourse === filter.idCourse;
    const matchYear = year === evaluation.year;
    return matchIdCSchool && matchCourse && matchYear;
  }),
);

export const getYears = (evaluations = []) => {
  if (!evaluations.length) return [];
  let auxYear = [];
  const evaluationsYears = uniqBy(evaluations.map((e) => moment(Date.parse(e.date)).year()))
    .sort()
    .reverse();
  if (!evaluationsYears.includes(moment(new Date()).year())) {
    auxYear.push(moment(new Date()).year());
  }

  auxYear = auxYear.concat(evaluationsYears);
  return auxYear;
};

export const getCurrentYear = (evaluations = []) => {
  if (!evaluations.length) return [];
  const currentYear = uniqBy(
    evaluations
      .map((e) => moment(Date.parse(e.date) || new Date()).year())
      .sort()
      .reverse(),
  );

  return currentYear[0];
};

export const getStatus = (evaluations = []) => uniqBy(evaluations.map((e) => e.status));

export const getStatusOptions = (status = [], intl) =>
  status.map((s) => ({
    label: messages[`status_${s}`] ? intl.formatMessage(messages[`status_${s}`]) : s,
    value: s,
  }));

export const getApplicationOptions = (applications = [], intl) =>
  applications.map((a) => ({
    label: messages[`application_${a}`] ? intl.formatMessage(messages[`application_${a}`]) : a,
    value: a,
  }));

export const getCourseOptions = (groups, idCSchool) => {
  if (!groups || !idCSchool) return [];
  return groups
    .filter((g) => g.idCSchool === idCSchool)
    .map((g) => ({ label: `${g.levelName} - ${g.courseName}`, value: g.idCourse }));
};

export function getSchoolOptions(groups, idContract) {
  if (!groups || groups.length === 0) return [];
  const allSchools = groups
    .filter((g) => !idContract || Number(g.idContract) === Number(idContract))
    .map((g) => {
      let label = g.schoolName;
      if (g.schoolCity) {
        label += ` - ${g.schoolCity}`;
      }
      return {
        value: g.idCSchool,
        label: label
      }
  });
  return uniqBy(allSchools, (s) => s.value);
}
export const getSchoolCSOptions = (evaluation) => {
  if (!evaluation) return [];
  return evaluation.schools.map((s) => ({ value: s.idCSchool, label: s.name }));
};

export const getApplications = (evaluations = []) => uniqBy(evaluations.map((e) => e.application));

export const getEvaluations = (evaluations = []) =>
  evaluations.map((e) => ({
    ...e,
    year: moment(Date.parse(e.date) || new Date()).year(),
    title: e.levelSegment.join(', '),
    abbreviation: e.catalogings.map((c) => c.abbreviation).join(' | '),
  }));

export const getEvaluationInformation = (data) => {
  const titles = data.evaluation.catalogings.map((el) => el.name);

  if (titles.length === 0) {
    return data;
  }
  if (titles.length === 1) {
    data.title = first(titles);
  } else {
    const lastCataloging = titles[titles.length - 1];
    titles.pop();
    const catalogingsArray = titles.join(', ');
    const catalogings = catalogingsArray.concat(` e ${lastCataloging}`);
    data.title = catalogings;
  }

  return data;
};

export function getContracts(groups) {
  if (!groups || groups.length === 0) return [];
  const allContracts = groups.map((g) => pick(g, ['idContract', 'contractName']));
  return uniqBy(allContracts, (c) => c.idContract);
}

export function getContractOptions(groups) {
  if (!groups || groups.length === 0) return [];
  const allContracts = groups.map((g) => ({ value: g.idContract, label: g.contractName }));
  return uniqBy(allContracts, (c) => c.value);
}

export function getClassOptions(groups, idCSchool) {
  if (!groups || groups.length === 0) return [];
  const allClasses = groups
    .filter((g) => !idCSchool || Number(g.idCSchool) === Number(idCSchool))
    .map((g) => ({ value: g.idCourse, label: `${g.levelName} - ${g.courseName} - ${g.period}` }));
  return uniqBy(allClasses, (c) => c.value);
}

export function getInitials(name) {
  if (!name) return '';
  const parts = name.replace(/[^\w\s]/gi, '').split(' ');
  let initials = '';
  for (let i = 0; i < parts.length; i++) {
    if (parts[i].length > 0 && parts[i] !== '' && isNaN(parts[i]) && initials.length < 2) {
      initials += parts[i][0];
    }
  }

  initials = initials.toUpperCase();
  return initials;
}
